/**
 * Returns the full asbolute URL of an avatar image, based on the relative path in the DB for the user..
 * @param image - The relative path to the image in the DB.
 * @param size - The size of the image.
 * @returns The URL of the avatar image.
 */
export default function avatarUrl(image: string | null | undefined, size: "40" | "70" | "original" = "70") {
    if (!image) return "";

    if (image.startsWith("http://") || image.startsWith("https://")) {
        return image;
    }

    const url = `${import.meta.env.VITE_S3_BASE_URL}/cdn-cgi/image/width=${size === "original" ? "800" : size},format=webp,quality=80/${image}`;
    return url;
}

export function taskFileUrl({
    taskId,
    filename,
    contentType,
    fullWidth = false,
}: {
    taskId: number;
    filename: string;
    contentType: string;
    fullWidth?: boolean;
}) {
    if (!taskId || !filename) return "";

    const isImage = contentType.startsWith("image/");
    if (isImage && !fullWidth) {
        const url = `${import.meta.env.VITE_S3_BASE_URL}/cdn-cgi/image/width=200,format=webp,quality=80/task-files/${taskId}/${filename}`;
        return url;
    }

    const url = `${import.meta.env.VITE_S3_BASE_URL}/task-files/${taskId}/${filename}`;
    return url;
}

const S3_BASE = import.meta.env.VITE_S3_BASE_URL as string | undefined;

/** Rewrite an image URL through Cloudflare Image Resizing (webp, max 3000px). */
export function cfResizedUrl(src: string, width: number = 3000, height: number = 3000): string {
    if (!S3_BASE) return src;

    // Already a resized URL — strip existing options and re-apply
    const cfPrefix = `${S3_BASE}/cdn-cgi/image/`;
    if (src.startsWith(cfPrefix)) {
        const path = src.slice(cfPrefix.length).replace(/^[^/]+\//, "");
        return `${S3_BASE}/cdn-cgi/image/width=${width},height=${height},fit=scale-down,format=webp,quality=85/${path}`;
    }

    // Same-origin image without resizing
    if (src.startsWith(S3_BASE)) {
        const path = src.slice(S3_BASE.length + 1);
        return `${S3_BASE}/cdn-cgi/image/width=${width},height=${height},fit=scale-down,format=webp,quality=85/${path}`;
    }

    // External URL — can't resize
    return src;
}
